import { useState, useRef, useEffect, useCallback } from 'react'
import { Icon, Tooltip } from '../atoms'

export interface CompassProps {
  heading: number | null
  mapMode: 'north' | 'follow'
  onMapModeToggle?: () => void
  onManualRotation?: (angle: number) => void
  manualRotation?: number | null
  isLocked?: boolean
  onLockToggle?: () => void
}

export const Compass = ({
  heading,
  mapMode,
  onManualRotation,
  manualRotation,
  isLocked = false,
  onLockToggle,
}: CompassProps) => {
  const compassRef = useRef<HTMLDivElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [rotationAngle, setRotationAngle] = useState(manualRotation || 0)
  const startAngleRef = useRef(0)
  const startRotationRef = useRef(0)
  const touchStartDistanceRef = useRef(0)
  const touchStartAngleRef = useRef(0)
  const touchStartRotationRef = useRef(0)

  // Update rotation angle when manualRotation prop changes
  useEffect(() => {
    if (manualRotation !== null && manualRotation !== undefined) {
      setRotationAngle(manualRotation)
    }
  }, [manualRotation])

  // Calculate compass display angle
  // When locked, show the manual rotation angle (or current rotation if no manual rotation set)
  // When in follow mode with heading, show heading
  // Otherwise show rotation angle or 0 for north
  const displayAngle = isLocked
    ? (manualRotation != null ? manualRotation : rotationAngle)
    : mapMode === 'follow' && heading != null 
    ? heading 
    : mapMode === 'north' 
    ? 0 
    : rotationAngle

  // Handle mouse drag for rotation (desktop)
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (isLocked) {
      // If locked, clicking unlocks it
      if (onLockToggle) {
        onLockToggle()
      }
      return
    }
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
    if (!compassRef.current) return

    const rect = compassRef.current.getBoundingClientRect()
    const centerX = rect.left + rect.width / 2
    const centerY = rect.top + rect.height / 2
    const angle = Math.atan2(e.clientY - centerY, e.clientX - centerX) * (180 / Math.PI)
    
    startAngleRef.current = angle
    startRotationRef.current = manualRotation != null ? manualRotation : rotationAngle
  }, [isLocked, rotationAngle, manualRotation, onLockToggle])

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging || !compassRef.current || isLocked) return

    const rect = compassRef.current.getBoundingClientRect()
    const centerX = rect.left + rect.width / 2
    const centerY = rect.top + rect.height / 2
    const angle = Math.atan2(e.clientY - centerY, e.clientX - centerX) * (180 / Math.PI)
    
    const delta = angle - startAngleRef.current
    let newRotation = startRotationRef.current + delta
    
    // Normalize to 0-360
    newRotation = ((newRotation % 360) + 360) % 360
    
    setRotationAngle(newRotation)
    onManualRotation?.(newRotation)
  }, [isDragging, isLocked, onManualRotation])

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }
    }
  }, [isDragging, handleMouseMove, handleMouseUp])

  // Handle touch for 2-finger rotation (mobile)
  const getTouchDistance = (touch1: Touch, touch2: Touch) => {
    const dx = touch2.clientX - touch1.clientX
    const dy = touch2.clientY - touch1.clientY
    return Math.sqrt(dx * dx + dy * dy)
  }

  const getTouchAngle = (touch1: Touch, touch2: Touch) => {
    const dx = touch2.clientX - touch1.clientX
    const dy = touch2.clientY - touch1.clientY
    return Math.atan2(dy, dx) * (180 / Math.PI)
  }

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (isLocked || e.touches.length !== 2) return
    e.preventDefault()
    
    const touch1 = e.touches[0] as unknown as Touch
    const touch2 = e.touches[1] as unknown as Touch
    
    touchStartDistanceRef.current = getTouchDistance(touch1, touch2)
    touchStartAngleRef.current = getTouchAngle(touch1, touch2)
    touchStartRotationRef.current = manualRotation != null ? manualRotation : rotationAngle
  }, [isLocked, rotationAngle, manualRotation])

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (e.touches.length !== 2 || isLocked) return
    e.preventDefault()

    const touch1 = e.touches[0] as unknown as Touch
    const touch2 = e.touches[1] as unknown as Touch
    
    const currentAngle = getTouchAngle(touch1, touch2)
    const deltaAngle = currentAngle - touchStartAngleRef.current
    
    let newRotation = touchStartRotationRef.current + deltaAngle
    newRotation = ((newRotation % 360) + 360) % 360
    
    setRotationAngle(newRotation)
    onManualRotation?.(newRotation)
  }, [isLocked, onManualRotation])

  const handleTouchEnd = useCallback(() => {
    touchStartDistanceRef.current = 0
    touchStartAngleRef.current = 0
  }, [])

  useEffect(() => {
    const compass = compassRef.current
    if (!compass) return

    compass.addEventListener('touchmove', handleTouchMove as any, { passive: false })
    compass.addEventListener('touchend', handleTouchEnd)
    
    return () => {
      compass.removeEventListener('touchmove', handleTouchMove as any)
      compass.removeEventListener('touchend', handleTouchEnd)
    }
  }, [handleTouchMove, handleTouchEnd])


  // Calculate compass rose rotation (opposite of map rotation)
  // When map rotates, compass rose rotates to keep N pointing north
  const compassRoseRotation = isLocked && manualRotation != null
    ? -manualRotation
    : mapMode === 'north'
    ? 0
    : heading != null
    ? -heading
    : -rotationAngle

  return (
    <div className="absolute top-3 right-3 z-[800] flex flex-col items-end gap-2">
      {/* Main Compass */}
      <Tooltip
        content={
          isLocked
            ? 'Compass is locked. Click to unlock.'
            : mapMode === 'north'
            ? 'Switch to heading-up mode (map rotates with your direction)'
            : 'Switch to north-up mode (north always at top)'
        }
        position="left"
      >
        <div
          ref={compassRef}
          className={`relative w-[72px] h-[72px] rounded-full border-2 ${
            isLocked ? 'border-primary' : 'border-border'
          } bg-background-elevated/95 backdrop-blur-md flex items-center justify-center ${
            isLocked ? 'cursor-pointer' : isDragging ? 'cursor-grabbing' : 'cursor-grab'
          } select-none shadow-lg transition-all ${
            isDragging ? 'scale-95' : 'hover:scale-105'
          }`}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          role="button"
          tabIndex={0}
          aria-label={isLocked ? "Compass locked - click to unlock" : "Compass - drag to rotate map"}
        >
          {/* Compass Rose - rotates with map to keep N pointing north */}
          <div 
            className="absolute inset-0 rounded-full overflow-visible"
            style={{
              transform: `rotate(${compassRoseRotation}deg)`,
              transition: isDragging ? 'none' : 'transform 0.3s ease-out',
            }}
          >
            {/* Outer ring with gradient */}
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-background-elevated to-background-card/50" />
            
            {/* Cardinal directions with better styling */}
            <div className="absolute top-1.5 left-1/2 -translate-x-1/2 text-[11px] font-bold text-primary drop-shadow-sm">
              N
            </div>
            <div className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] font-semibold text-text">E</div>
            <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 text-[10px] font-semibold text-text">S</div>
            <div className="absolute left-1.5 top-1/2 -translate-y-1/2 text-[10px] font-semibold text-text">W</div>
            
            {/* Direction markers - thicker for visibility */}
            <div className="absolute top-0 left-1/2 w-1 h-3 bg-primary -translate-x-1/2 rounded-sm" />
            <div className="absolute right-0 top-1/2 w-3 h-1 bg-text/50 -translate-y-1/2 rounded-sm" />
            <div className="absolute bottom-0 left-1/2 w-1 h-3 bg-text/50 -translate-x-1/2 rounded-sm" />
            <div className="absolute left-0 top-1/2 w-3 h-1 bg-text/50 -translate-y-1/2 rounded-sm" />
            
            {/* Intermediate direction dots */}
            <div className="absolute top-3 right-3 w-1.5 h-1.5 bg-text/40 rounded-full" />
            <div className="absolute top-3 left-3 w-1.5 h-1.5 bg-text/40 rounded-full" />
            <div className="absolute bottom-3 right-3 w-1.5 h-1.5 bg-text/40 rounded-full" />
            <div className="absolute bottom-3 left-3 w-1.5 h-1.5 bg-text/40 rounded-full" />
          </div>
          
          {/* Compass Arrow/Needle - points to current heading/direction, rotates smoothly */}
          <div 
            className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none"
            style={{
              transform: `rotate(${displayAngle}deg)`,
              transition: isDragging ? 'none' : 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            {/* Main arrow pointing up (north) - larger and more visible */}
            <div className="relative w-0 h-0">
              {/* Arrow head (red/north) - larger and more prominent */}
              <div 
                className="absolute -top-8 left-1/2 -translate-x-1/2 w-0 h-0"
                style={{
                  borderLeft: '7px solid transparent',
                  borderRight: '7px solid transparent',
                  borderBottom: '24px solid #ef4444',
                  filter: 'drop-shadow(0 3px 6px rgba(0,0,0,0.5))',
                }}
              />
              {/* Arrow tail (white/south) - balanced */}
              <div 
                className="absolute top-4 left-1/2 -translate-x-1/2 w-0 h-0"
                style={{
                  borderLeft: '5px solid transparent',
                  borderRight: '5px solid transparent',
                  borderTop: '18px solid #e5e7eb',
                  filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.4))',
                }}
              />
              {/* Arrow center line for better visibility */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-0.5 h-8 bg-text/20" />
            </div>
          </div>
          
          {/* Center dot - larger and more visible */}
          <div className="absolute w-2.5 h-2.5 rounded-full bg-text z-20 shadow-lg border border-background-elevated" />
          
          {/* Lock indicator */}
          {isLocked && (
            <div className="absolute -top-1 -right-1 w-5 h-5 bg-primary rounded-full flex items-center justify-center shadow-lg z-30">
              <Icon name="location" className="w-3 h-3 text-white" />
            </div>
          )}
        </div>
      </Tooltip>

    </div>
  )
}
